import { useSession, useSessionList } from "@clerk/clerk-react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import GeneratedAvatar from "@/components/users/GeneratedAvatar";
import { API_BASE_URL } from "@/config/endpoints";
import { useActivateActorToken } from "@/hooks/useImpersonate";
import type { ImpersonationStartResponse, UserListResponse } from "@/interfaces/user";

interface SwitchUserModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * SwitchUserModal — search-and-select modal that lets a GFG admin switch
 * directly to impersonating a different user without having to stop first.
 *
 * It intentionally defines its own inline `useQuery` and `useMutation`
 * instead of reusing existing hooks because it must specifically retrieve
 * and use the original admin session token, rather than the active session.
 *
 * Design matches the demo's "Switch user" modal:
 * - Crimson title ("Switch user"), subtitle, search input, scrollable results.
 * - Each row: GeneratedAvatar, name, email · company, eye icon.
 * - Close button (top-right).
 */
export default function SwitchUserModal({ open, onClose }: SwitchUserModalProps) {
  const { session } = useSession();
  const { sessions } = useSessionList();
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced search — only hit the API after the user pauses typing
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch users matching the search using the original admin token
  const { data, isLoading } = useQuery({
    queryKey: ["users-switch-modal", debouncedSearch],
    queryFn: async () => {
      const adminSession =
        sessions?.find((s) => s.user?.id === session?.actor?.sub) ||
        sessions?.find((s) => !s.actor);
      if (!adminSession) throw new Error("No admin session found");
      const token = await adminSession.getToken({ template: "gfgf-api" });
      if (!token) throw new Error("Failed to retrieve admin token");

      const { data } = await axios.get<UserListResponse>(`${API_BASE_URL}/admin/users`, {
        params: { search: debouncedSearch || undefined, limit: 50 },
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    },
    enabled: open, // only fetch when modal is open
  });
  const users = data?.data ?? [];

  const impersonate = useMutation({
    mutationFn: async ({ targetUserId }: { targetUserId: string }) => {
      const adminSession =
        sessions?.find((s) => s.user?.id === session?.actor?.sub) ||
        sessions?.find((s) => !s.actor);
      if (!adminSession) throw new Error("No admin session found");
      const token = await adminSession.getToken({ template: "gfgf-api" });
      if (!token) throw new Error("Failed to retrieve admin token");

      const { data } = await axios.post<ImpersonationStartResponse>(
        `${API_BASE_URL}/admin/users/impersonation/start`,
        { targetUserId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return data;
    },
  });

  // Reset search when opening
  useEffect(() => {
    if (open) {
      setSearch("");
      setDebouncedSearch("");
      // Auto-focus the search input
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const { activate } = useActivateActorToken();

  const handleSelect = (clerkUserId: string) => {
    if (!clerkUserId || impersonate.isPending) return;
    impersonate.mutate(
      { targetUserId: clerkUserId },
      {
        onSuccess: async (res) => {
          const ticket = res.actorToken?.token;
          if (!ticket) return;
          try {
            // Signs the new actor session in on this origin (never the Account Portal's redirect
            // to staging) and removes the current impersonation session on the way.
            await activate(ticket, { replaceSession: session });
            onClose();
          } catch (e) {
            console.error("Failed to switch impersonated user", e);
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            position: "relative",
            borderRadius: "12px",
            border: "1px solid var(--color-smoke)",
            p: "32px",
            m: 2,
            width: 480,
            maxWidth: "calc(100vw - 40px)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          },
        },
      }}
    >
      {/* Close button — top right, matching demo's .imp-modal-close */}
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{
          position: "absolute",
          top: 18,
          right: 18,
          width: 36,
          height: 36,
          border: "1px solid var(--color-smoke)",
          borderRadius: "8px",
          bgcolor: "var(--color-white)",
          color: "var(--color-graphite)",
          transition: "background 0.15s, color 0.15s",
          "&:hover": {
            bgcolor: "var(--color-wax)",
            color: "var(--color-rodeo-black)",
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>

      {/* Title */}
      <Typography
        component="h2"
        sx={{
          fontFamily: "var(--font-crimson)",
          fontSize: "24px",
          fontWeight: 400,
          color: "var(--color-rodeo-black)",
          mb: "6px",
        }}
      >
        Switch user
      </Typography>

      {/* Subtitle */}
      <Typography
        sx={{
          fontFamily: "var(--font-lato)",
          fontSize: "14px",
          color: "var(--color-graphite)",
          mb: "16px",
          lineHeight: 1.5,
        }}
      >
        Search for another person to impersonate.
      </Typography>

      {/* Search input — matches demo's .ga-search style */}
      <TextField
        inputRef={inputRef}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name, email, or company"
        aria-label="Search users to impersonate"
        fullWidth
        size="small"
        type="search"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "var(--color-graphite)" }} />
              </InputAdornment>
            ),
            sx: {
              fontFamily: "var(--font-lato)",
              fontSize: "14px",
              color: "var(--color-rodeo-black)",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "var(--color-smoke)",
              },
              "&:hover fieldset": {
                borderColor: "var(--color-rodeo-dust) !important",
              },
              "&.Mui-focused fieldset": {
                borderColor: "var(--color-dark-dust) !important",
                borderWidth: "1px !important",
              },
            },
          },
        }}
        sx={{ mb: 1 }}
      />

      {/* Results list — matching demo's .imp-search-results */}
      <Box
        sx={{
          mt: 1,
          minHeight: 320,
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress
              aria-label="Loading"
              size={28}
              sx={{ color: "var(--color-dark-dust)" }}
            />
          </Box>
        ) : users.length === 0 ? (
          <Typography
            sx={{
              py: 2,
              px: "12px",
              fontSize: "14px",
              color: "var(--color-graphite)",
            }}
          >
            No users match your search.
          </Typography>
        ) : (
          users
            .filter((u) => !!u.clerkUserId)
            .map((u) => (
              <Box
                key={u.id}
                component="button"
                onClick={() => handleSelect(u.clerkUserId!)}
                disabled={impersonate.isPending}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  width: "100%",
                  textAlign: "left",
                  p: "10px 12px",
                  borderRadius: "8px",
                  cursor: impersonate.isPending ? "wait" : "pointer",
                  bgcolor: "transparent",
                  border: "none",
                  fontFamily: "var(--font-lato)",
                  transition: "background 0.1s",
                  "&:hover": { bgcolor: "var(--color-wax)" },
                  "&:disabled": { opacity: 0.6 },
                }}
              >
                {/* Avatar */}
                <GeneratedAvatar id={u.id} size={28} alt={u.name} />

                {/* Name + email · company */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "var(--color-rodeo-black)",
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {u.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "var(--color-graphite)",
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {u.email}
                    {u.organization ? ` · ${u.organization}` : ""}
                  </Typography>
                </Box>

                {/* Eye icon on the right */}
                <VisibilityIcon
                  sx={{ fontSize: 20, color: "var(--color-dark-dust)", flexShrink: 0 }}
                />
              </Box>
            ))
        )}
      </Box>
    </Dialog>
  );
}
